<template>
    <!-- Row variant: horizontal button layout -->
    <div
        v-if="variant === 'row'"
        class="flex flex-col align-center md:items-center md:flex-row md:flex-wrap gap-2 mr-0.5"
        :class="props.left ? 'justify-start' : 'justify-end'">
        <div class="flex flex-col align-center md:flex-row md:flex-wrap md:justify-end gap-2">
            <Button
                v-if="props.showBack"
                variant="outline"
                :size="buttonSize"
                :disabled="props.disabled || props.loading || props.disableBack"
                @click="onEmit('back')">
                <ChevronLeft class="h-4 w-4 text-primary" />
                Back
            </Button>

            <Button
                v-if="props.showNext"
                variant="outline"
                :size="buttonSize"
                :disabled="props.disabled || props.loading || props.disableNext"
                @click="onEmit('next')">
                <ChevronRight class="h-4 w-4 text-primary" />
                Next
            </Button>

            <Button
                v-if="props.showView"
                variant="outline"
                :size="buttonSize"
                :disabled="props.disabled || props.loading || props.disableView"
                @click="onEmit('view')">
                <Columns class="h-4 w-4 text-primary" />
                View
            </Button>

            <Button
                v-if="props.showEdit"
                variant="outline"
                :size="buttonSize"
                :disabled="props.disabled || props.loading || props.disableEdit"
                @click="onEmit('edit')">
                <Edit class="h-4 w-4 text-accent-foreground" />
                Edit
            </Button>

            <Button
                v-if="props.showCopyId"
                variant="outline"
                :size="buttonSize"
                :disabled="props.disabled || props.loading || props.disableCopyId"
                @click="onEmit('copyId')">
                <Clipboard class="h-4 w-4 text-muted-foreground" />
                Copy ID
            </Button>

            <Button
                v-if="props.showSave"
                variant="outline"
                :size="buttonSize"
                class="bg-accent/10 hover:bg-accent/20"
                :disabled="props.disabled || props.loading || props.disableSave"
                @click="onEmit('save')">
                <Check class="h-4 w-4 text-accent-foreground" />
                Save
            </Button>

            <Button
                v-if="props.showDiscard && props.showSave && !props.disableSave"
                variant="outline"
                :size="buttonSize"
                class="bg-destructive/10 hover:bg-destructive/20"
                :disabled="props.disabled || props.loading || props.disableSave || props.disableDiscard"
                @click="onEmit('discard')">
                <X class="h-4 w-4 text-destructive" />
                Discard
            </Button>

            <Button
                v-can-access.permissions="[$Permissions.TRIGGER_ACTION_SOURCES]"
                v-if="props.showSnapshot"
                variant="outline"
                :size="buttonSize"
                class="h-8"
                :disabled="props.disabled || props.loading || props.disableSnapshot"
                @click="onEmit('snapshot')">
                <Video class="mr-1 h-4 w-4 text-chart-4" />
                Snapshot
            </Button>

            <Button
                v-can-access.permissions="[$Permissions.TRIGGER_ACTION_SOURCES]"
                v-if="props.showCancelSnapshot"
                variant="outline"
                :size="buttonSize"
                class="h-8"
                :disabled="props.disabled || props.loading || props.disableCancelSnapshot"
                @click="onEmit('cancelSnapshot')">
                <VideoOff class="mr-1 h-4 w-4 text-muted-foreground" />
                Cancel Snapshot
            </Button>

            <Button
                v-if="props.showPause"
                variant="outline"
                :size="buttonSize"
                :disabled="props.disabled || props.loading || props.disablePause"
                @click="onEmit('pause')">
                <Pause class="h-4 w-4 text-accent-foreground" />
                Pause
            </Button>

            <Button
                v-if="props.showStop"
                variant="outline"
                :size="buttonSize"
                :disabled="props.disabled || props.loading || props.disableStop"
                @click="onEmit('stop')">
                <Square class="h-4 w-4 text-destructive" />
                Stop
            </Button>

            <Button
                v-can-access.permissions="[$Permissions.TRIGGER_ACTION_SOURCES]"
                v-if="props.showPauseSource"
                variant="outline"
                :size="buttonSize"
                :disabled="props.disabled || props.loading || props.disablePauseSource"
                @click="onEmit('pauseSource')">
                <Pause class="h-4 w-4 text-accent-foreground" />
                Pause source
            </Button>

            <Button
                v-can-access.permissions="[$Permissions.TRIGGER_ACTION_SOURCES]"
                v-if="props.showStopSource"
                variant="outline"
                :size="buttonSize"
                :disabled="props.disabled || props.loading || props.disableStopSource"
                @click="onEmit('stopSource')">
                <Pause class="h-4 w-4 text-destructive" />
                Stop source
            </Button>

            <Button
                v-can-access.permissions="[$Permissions.TRIGGER_ACTION_SOURCES]"
                v-if="props.showResumeSource"
                variant="outline"
                :size="buttonSize"
                :disabled="props.disabled || props.loading || props.disableResumeSource"
                @click="onEmit('resumeSource')">
                <Play class="h-4 w-4 text-accent-foreground" />
                Resume source
            </Button>

            <Button
                v-can-access.permissions="[$Permissions.TRIGGER_ACTION_DESTINATIONS]"
                v-if="props.showPauseDestination"
                variant="outline"
                :size="buttonSize"
                :disabled="props.disabled || props.loading || props.disablePauseDestination"
                @click="onEmit('pauseDestination')">
                <Pause class="h-4 w-4 text-accent-foreground" />
                Pause destination
            </Button>

            <Button
                v-can-access.permissions="[$Permissions.TRIGGER_ACTION_DESTINATIONS]"
                v-if="props.showStopDestination"
                variant="outline"
                :size="buttonSize"
                :disabled="props.disabled || props.loading || props.disableStopDestination"
                @click="onEmit('stopDestination')">
                <Square class="h-4 w-4 text-destructive" />
                Stop destination
            </Button>

            <Button
                v-can-access.permissions="[$Permissions.TRIGGER_ACTION_DESTINATIONS]"
                v-if="props.showResumeDestination"
                variant="outline"
                :size="buttonSize"
                :disabled="props.disabled || props.loading || props.disableResumeDestination"
                @click="onEmit('resumeDestination')">
                <Play class="h-4 w-4 text-accent-foreground" />
                Resume destination
            </Button>

            <Button
                v-if="props.showResume"
                variant="outline"
                :size="buttonSize"
                :disabled="props.disabled || props.loading || props.disableResume"
                @click="onEmit('resume')">
                <Play class="h-4 w-4 text-accent-foreground" />
                Resume
            </Button>

            <Button
                v-if="props.showRestart"
                variant="outline"
                :size="buttonSize"
                :disabled="props.disabled || props.loading || props.disableRestart"
                @click="onEmit('restart')">
                <RotateCcw class="h-4 w-4 text-destructive" />
                Restart
            </Button>

            <Button
                v-if="props.showReset"
                variant="outline"
                :size="buttonSize"
                class="bg-destructive/10 hover:bg-destructive/20"
                :disabled="props.disabled || props.loading || props.disableReset"
                @click="onEmit('reset')">
                <RefreshCcw class="h-4 w-4 text-destructive" />
                Reset
            </Button>

            <Button
                v-can-access.permissions="[$Permissions.TRIGGER_ACTION_SOURCES]"
                v-if="props.showResetSource"
                variant="outline"
                :size="buttonSize"
                class="bg-destructive/10 hover:bg-destructive/20"
                :disabled="props.disabled || props.loading || props.disableResetSource"
                @click="onEmit('resetSource')">
                <RefreshCcw class="h-4 w-4 text-destructive" />
                Reset source
            </Button>

            <Button
                v-can-access.permissions="[$Permissions.TRIGGER_ACTION_DESTINATIONS]"
                v-if="props.showResetDestination"
                variant="outline"
                :size="buttonSize"
                class="bg-destructive/10 hover:bg-destructive/20"
                :disabled="props.disabled || props.loading || props.disableResetDestination"
                @click="onEmit('resetDestination')">
                <RefreshCcw class="h-4 w-4 text-destructive" />
                Reset destination
            </Button>

            <Button
                v-if="props.showClone"
                variant="outline"
                :size="buttonSize"
                :disabled="props.disabled || props.loading || props.disableClone"
                @click="onEmit('clone')">
                <Copy class="h-4 w-4 text-primary" />
                Clone
            </Button>

            <Button
                v-if="props.showLogs"
                variant="outline"
                :size="buttonSize"
                :disabled="props.disabled || props.loading || props.disableLogs"
                @click="onEmit('logs')">
                <Code class="h-4 w-4 text-muted-foreground" />
                Logs
            </Button>

            <Button
                v-if="props.showViews"
                variant="outline"
                :size="buttonSize"
                :disabled="props.disabled || props.loading || props.disableViews"
                @click="onEmit('views')">
                <ScanLine class="h-4 w-4 text-primary" />
                Views
            </Button>

            <Button
                v-if="props.showDownloadViews"
                variant="outline"
                :size="buttonSize"
                :disabled="props.disabled || props.loading || props.disableDownloadViews"
                @click="onEmit('downloadViews')">
                <ScanLine class="h-4 w-4 text-primary" />
                Download All Views
            </Button>

            <Button
                v-if="props.showAddInputTableSourceTopics"
                variant="outline"
                :size="buttonSize"
                :disabled="props.disabled || props.loading || props.disableAddInputTableSourceTopics"
                @click="onEmit('addInputTableSourceTopics')">
                <Plus class="h-4 w-4 text-accent-foreground" />
                Input Table/Source Topics
            </Button>

            <Button
                v-if="props.showAddOutputTableSinkTopics"
                variant="outline"
                :size="buttonSize"
                :disabled="props.disabled || props.loading || props.disableAddOutputTableSinkTopics"
                @click="onEmit('addOutputTableSinkTopics')">
                <Plus class="h-4 w-4 text-accent-foreground" />
                Output Table/Sink Topics
            </Button>

            <Button
                v-if="props.showExportUsage"
                variant="outline"
                :size="buttonSize"
                :disabled="props.disabled || props.loading || props.disableExportUsage"
                @click="onEmit('exportUsage')">
                <Download class="h-4 w-4 text-muted-foreground" />
                Export Usage
            </Button>

            <Button
                v-if="props.showDelete"
                variant="outline"
                :size="buttonSize"
                :disabled="props.disabled || props.loading || props.disableDelete"
                @click="onEmit('delete')">
                <MinusCircle class="h-4 w-4 text-destructive" />
                Delete
            </Button>

            <Button
                v-if="props.showMarkAll"
                variant="outline"
                :size="buttonSize"
                :disabled="props.disabled || props.loading || props.disableMarkAll"
                @click="onEmit('markAll')">
                <MailOpen class="h-4 w-4 text-primary" />
                Mark all as read
            </Button>

            <!-- Table-specific actions -->
            <Button v-if="props.showRead" variant="outline" :size="buttonSize" :disabled="props.disabled || props.loading" @click="onEmit('read')">
                <MailOpen class="mr-1 h-4 w-4 text-muted-foreground" />
                Mark as read
            </Button>

            <Button
                v-if="props.showUnread"
                variant="outline"
                :size="buttonSize"
                :disabled="props.disabled || props.loading"
                @click="onEmit('unread')">
                <Mail class="mr-1 h-4 w-4 text-muted-foreground" />
                Mark as unread
            </Button>

            <Button
                v-if="props.showTagsUpdate"
                variant="outline"
                :size="buttonSize"
                :disabled="props.disabled || props.loading || props.disableTagsUpdate"
                @click="onEmit('updateTags')">
                <Tag class="mr-1 h-4 w-4 text-primary" />
                Update Tags
            </Button>

            <Button
                v-if="props.showCopyKafkaBrokers"
                variant="outline"
                :size="buttonSize"
                :disabled="props.disabled || props.loading || props.disableCopyKafkaBrokers"
                @click="onEmit('copyKafkaBrokers')">
                <Clipboard class="mr-1 h-4 w-4 text-muted-foreground" />
                Copy Kafka Brokers
            </Button>

            <Button
                v-if="props.showApiTokens"
                variant="outline"
                :size="buttonSize"
                :disabled="props.disabled || props.loading || props.disableApiTokens"
                @click="onEmit('apiTokens')">
                <Edit class="mr-1 h-4 w-4 text-primary" />
                API Tokens
            </Button>
        </div>

        <div v-if="props.loading" class="flex items-center">
            <LoaderCircle class="h-4 w-4 animate-spin" />
        </div>
    </div>

    <!-- Menu variant: dropdown with 3-dots trigger -->
    <DropdownMenu v-else-if="variant === 'menu'">
        <DropdownMenuTrigger as-child>
            <Button
                v-if="shouldShowAsButton"
                variant="outline"
                size="sm"
                :disabled="props.disabled || props.loading"
                :class="`${menuTriggerClasses.button.split(' ')[0]} px-3 gap-2`">
                <LoaderCircle v-if="props.loading" :class="`${menuTriggerClasses.icon} animate-spin`" />
                <component v-else :is="triggerIcon" :class="menuTriggerClasses.icon" />
                {{ triggerLabel }}
            </Button>
            <Button
                v-else
                variant="ghost"
                size="sm"
                :disabled="props.disabled || props.loading"
                :class="`${menuTriggerClasses.button} p-0 hover:bg-accent/60`">
                <LoaderCircle v-if="props.loading" :class="`${menuTriggerClasses.icon} animate-spin`" />
                <component
                    v-else
                    :is="triggerIcon"
                    :class="`${menuTriggerClasses.icon} text-foreground/70 hover:text-foreground transition-colors`" />
            </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" class="w-48">
            <DropdownMenuItem v-if="props.showBack" :disabled="props.disabled || props.loading || props.disableBack" @click="onEmit('back')">
                <ChevronLeft class="h-4 w-4 text-primary" />
                Back
            </DropdownMenuItem>

            <DropdownMenuItem v-if="props.showNext" :disabled="props.disabled || props.loading || props.disableNext" @click="onEmit('next')">
                <ChevronRight class="h-4 w-4 text-primary" />
                Next
            </DropdownMenuItem>

            <DropdownMenuItem v-if="props.showView" :disabled="props.disabled || props.loading || props.disableView" @click="onEmit('view')">
                <Columns class="h-4 w-4 text-primary" />
                View
            </DropdownMenuItem>

            <DropdownMenuItem v-if="props.showEdit" :disabled="props.disabled || props.loading || props.disableEdit" @click="onEmit('edit')">
                <Edit class="h-4 w-4 text-accent-foreground" />
                Edit
            </DropdownMenuItem>

            <DropdownMenuItem v-if="props.showCopyId" :disabled="props.disabled || props.loading || props.disableCopyId" @click="onEmit('copyId')">
                <Clipboard class="h-4 w-4 text-muted-foreground" />
                Copy ID
            </DropdownMenuItem>

            <DropdownMenuItem v-if="props.showSave" :disabled="props.disabled || props.loading || props.disableSave" @click="onEmit('save')">
                <Check class="h-4 w-4 text-accent-foreground" />
                Save
            </DropdownMenuItem>

            <DropdownMenuItem
                v-if="props.showDiscard && props.showSave && !props.disableSave"
                :disabled="props.disabled || props.loading || props.disableSave || props.disableDiscard"
                @click="onEmit('discard')">
                <X class="h-4 w-4 text-destructive" />
                Discard
            </DropdownMenuItem>

            <DropdownMenuItem
                v-can-access.permissions="[$Permissions.TRIGGER_ACTION_SOURCES]"
                v-if="props.showSnapshot"
                :disabled="props.disabled || props.loading || props.disableSnapshot"
                @click="onEmit('snapshot')">
                <Video class="mr-1 h-4 w-4 text-chart-4" />
                Snapshot
            </DropdownMenuItem>

            <DropdownMenuItem
                v-can-access.permissions="[$Permissions.TRIGGER_ACTION_SOURCES]"
                v-if="props.showCancelSnapshot"
                :disabled="props.disabled || props.loading || props.disableCancelSnapshot"
                @click="onEmit('cancelSnapshot')">
                <VideoOff class="mr-1 h-4 w-4 text-muted-foreground" />
                Cancel Snapshot
            </DropdownMenuItem>

            <DropdownMenuItem v-if="props.showPause" :disabled="props.disabled || props.loading || props.disablePause" @click="onEmit('pause')">
                <Pause class="h-4 w-4 text-accent-foreground" />
                Pause
            </DropdownMenuItem>

            <DropdownMenuItem v-if="props.showStop" :disabled="props.disabled || props.loading || props.disableStop" @click="onEmit('stop')">
                <Square class="h-4 w-4 text-destructive" />
                Stop
            </DropdownMenuItem>

            <DropdownMenuItem
                v-can-access.permissions="[$Permissions.TRIGGER_ACTION_SOURCES]"
                v-if="props.showPauseSource"
                :disabled="props.disabled || props.loading || props.disablePauseSource"
                @click="onEmit('pauseSource')">
                <Pause class="h-4 w-4 text-accent-foreground" />
                Pause source
            </DropdownMenuItem>

            <DropdownMenuItem
                v-can-access.permissions="[$Permissions.TRIGGER_ACTION_SOURCES]"
                v-if="props.showStopSource"
                :disabled="props.disabled || props.loading || props.disableStopSource"
                @click="onEmit('stopSource')">
                <Pause class="h-4 w-4 text-destructive" />
                Stop source
            </DropdownMenuItem>

            <DropdownMenuItem
                v-can-access.permissions="[$Permissions.TRIGGER_ACTION_SOURCES]"
                v-if="props.showResumeSource"
                :disabled="props.disabled || props.loading || props.disableResumeSource"
                @click="onEmit('resumeSource')">
                <Play class="h-4 w-4 text-accent-foreground" />
                Resume source
            </DropdownMenuItem>

            <DropdownMenuItem
                v-can-access.permissions="[$Permissions.TRIGGER_ACTION_DESTINATIONS]"
                v-if="props.showPauseDestination"
                :disabled="props.disabled || props.loading || props.disablePauseDestination"
                @click="onEmit('pauseDestination')">
                <Pause class="h-4 w-4 text-accent-foreground" />
                Pause destination
            </DropdownMenuItem>

            <DropdownMenuItem
                v-can-access.permissions="[$Permissions.TRIGGER_ACTION_DESTINATIONS]"
                v-if="props.showStopDestination"
                :disabled="props.disabled || props.loading || props.disableStopDestination"
                @click="onEmit('stopDestination')">
                <Square class="h-4 w-4 text-destructive" />
                Stop destination
            </DropdownMenuItem>

            <DropdownMenuItem
                v-can-access.permissions="[$Permissions.TRIGGER_ACTION_DESTINATIONS]"
                v-if="props.showResumeDestination"
                :disabled="props.disabled || props.loading || props.disableResumeDestination"
                @click="onEmit('resumeDestination')">
                <Play class="h-4 w-4 text-accent-foreground" />
                Resume destination
            </DropdownMenuItem>

            <DropdownMenuItem v-if="props.showResume" :disabled="props.disabled || props.loading || props.disableResume" @click="onEmit('resume')">
                <Play class="h-4 w-4 text-accent-foreground" />
                Resume
            </DropdownMenuItem>

            <DropdownMenuItem v-if="props.showReset" :disabled="props.disabled || props.loading || props.disableReset" @click="onEmit('reset')">
                <RefreshCcw class="h-4 w-4 text-destructive" />
                Reset
            </DropdownMenuItem>

            <DropdownMenuItem
                v-can-access.permissions="[$Permissions.TRIGGER_ACTION_SOURCES]"
                v-if="props.showResetSource"
                :disabled="props.disabled || props.loading || props.disableResetSource"
                @click="onEmit('resetSource')">
                <RefreshCcw class="h-4 w-4 text-destructive" />
                Reset source
            </DropdownMenuItem>

            <DropdownMenuItem
                v-can-access.permissions="[$Permissions.TRIGGER_ACTION_DESTINATIONS]"
                v-if="props.showResetDestination"
                :disabled="props.disabled || props.loading || props.disableResetDestination"
                @click="onEmit('resetDestination')">
                <RefreshCcw class="h-4 w-4 text-destructive" />
                Reset destination
            </DropdownMenuItem>

            <DropdownMenuItem v-if="props.showClone" :disabled="props.disabled || props.loading || props.disableClone" @click="onEmit('clone')">
                <Copy class="h-4 w-4 text-primary" />
                Clone
            </DropdownMenuItem>

            <DropdownMenuItem v-if="props.showLogs" :disabled="props.disabled || props.loading || props.disableLogs" @click="onEmit('logs')">
                <Code class="h-4 w-4 text-muted-foreground" />
                Logs
            </DropdownMenuItem>

            <DropdownMenuItem v-if="props.showViews" :disabled="props.disabled || props.loading || props.disableViews" @click="onEmit('views')">
                <ScanLine class="h-4 w-4 text-primary" />
                Views
            </DropdownMenuItem>

            <DropdownMenuItem
                v-if="props.showDownloadViews"
                :disabled="props.disabled || props.loading || props.disableDownloadViews"
                @click="onEmit('downloadViews')">
                <ScanLine class="h-4 w-4 text-primary" />
                Download All Views
            </DropdownMenuItem>

            <DropdownMenuItem
                v-if="props.showAddInputTableSourceTopics"
                :disabled="props.disabled || props.loading || props.disableAddInputTableSourceTopics"
                @click="onEmit('addInputTableSourceTopics')">
                <Plus class="h-4 w-4 text-accent-foreground" />
                Input Table/Source Topics
            </DropdownMenuItem>

            <DropdownMenuItem
                v-if="props.showAddOutputTableSinkTopics"
                :disabled="props.disabled || props.loading || props.disableAddOutputTableSinkTopics"
                @click="onEmit('addOutputTableSinkTopics')">
                <Plus class="h-4 w-4 text-accent-foreground" />
                Output Table/Sink Topics
            </DropdownMenuItem>

            <DropdownMenuItem
                v-if="props.showExportUsage"
                :disabled="props.disabled || props.loading || props.disableExportUsage"
                @click="onEmit('exportUsage')">
                <Download class="h-4 w-4 text-muted-foreground" />
                Export Usage
            </DropdownMenuItem>

            <DropdownMenuItem v-if="props.showDelete" :disabled="props.disabled || props.loading || props.disableDelete" @click="onEmit('delete')">
                <MinusCircle class="h-4 w-4 text-destructive" />
                Delete
            </DropdownMenuItem>

            <DropdownMenuItem v-if="props.showMarkAll" :disabled="props.disabled || props.loading || props.disableMarkAll" @click="onEmit('markAll')">
                <MailOpen class="h-4 w-4 text-primary" />
                Mark all as read
            </DropdownMenuItem>

            <!-- Table-specific actions -->
            <DropdownMenuItem v-if="props.showRead" :disabled="props.disabled || props.loading" @click="onEmit('read')">
                <MailOpen class="mr-1 h-4 w-4 text-muted-foreground" />
                Mark as read
            </DropdownMenuItem>

            <DropdownMenuItem v-if="props.showUnread" :disabled="props.disabled || props.loading" @click="onEmit('unread')">
                <Mail class="mr-1 h-4 w-4 text-muted-foreground" />
                Mark as unread
            </DropdownMenuItem>

            <DropdownMenuItem
                v-if="props.showTagsUpdate"
                :disabled="props.disabled || props.loading || props.disableTagsUpdate"
                @click="onEmit('updateTags')">
                <Tag class="mr-1 h-4 w-4 text-primary" />
                Update Tags
            </DropdownMenuItem>

            <DropdownMenuItem
                v-if="props.showCopyKafkaBrokers"
                :disabled="props.disabled || props.loading || props.disableCopyKafkaBrokers"
                @click="onEmit('copyKafkaBrokers')">
                <Clipboard class="mr-1 h-4 w-4 text-muted-foreground" />
                Copy Kafka Brokers
            </DropdownMenuItem>

            <DropdownMenuItem
                v-if="props.showApiTokens"
                :disabled="props.disabled || props.loading || props.disableApiTokens"
                @click="onEmit('apiTokens')">
                <Edit class="mr-1 h-4 w-4 text-primary" />
                API Tokens
            </DropdownMenuItem>
        </DropdownMenuContent>
    </DropdownMenu>
</template>

<script setup>
import { computed } from 'vue';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import {
    Edit,
    MinusCircle,
    Columns,
    Play,
    RotateCcw,
    RefreshCcw,
    Pause,
    Square,
    Plus,
    Video,
    VideoOff,
    Copy,
    Code,
    ScanLine,
    Download,
    Check,
    X,
    ChevronLeft,
    ChevronRight,
    MailOpen,
    Mail,
    Clipboard,
    MoreHorizontal,
    Tag,
    LoaderCircle
} from 'lucide-vue-next';

const props = defineProps({
    // Variant: 'row' for horizontal buttons, 'menu' for dropdown
    variant: {
        type: String,
        default: 'row',
        validator: (value) => ['row', 'menu'].includes(value)
    },

    // For menu variant - customizable trigger
    triggerIcon: {
        type: [Object, Function],
        default: () => MoreHorizontal
    },
    triggerLabel: {
        type: String,
        default: ''
    },
    triggerAsButton: {
        type: Boolean,
        default: null // Will be computed based on triggerLabel if not explicitly set
    },

    loading: Boolean,
    disabled: Boolean,
    left: Boolean,

    // Action visibility props
    showNext: Boolean,
    disableNext: Boolean,

    showBack: Boolean,
    disableBack: Boolean,

    showView: Boolean,
    disableView: Boolean,

    showEdit: Boolean,
    disableEdit: Boolean,

    showSave: Boolean,
    disableSave: Boolean,

    showDiscard: {
        type: Boolean,
        default: true
    },
    disableDiscard: {
        type: Boolean,
        default: false
    },

    showPause: Boolean,
    disablePause: Boolean,

    showStop: Boolean,
    disableStop: Boolean,

    showResume: Boolean,
    disableResume: Boolean,

    showStopSource: Boolean,
    showPauseSource: Boolean,
    showResumeSource: Boolean,

    showStopDestination: Boolean,
    showPauseDestination: Boolean,
    showResumeDestination: Boolean,

    disableStopSource: Boolean,
    disablePauseSource: Boolean,
    disableResumeSource: Boolean,

    disableStopDestination: Boolean,
    disablePauseDestination: Boolean,
    disableResumeDestination: Boolean,

    showRestart: Boolean,
    disableRestart: Boolean,

    showReset: Boolean,
    disableReset: Boolean,

    showResetSource: Boolean,
    disableResetSource: Boolean,

    showResetDestination: Boolean,
    disableResetDestination: Boolean,

    showDelete: Boolean,
    disableDelete: Boolean,

    showLogs: Boolean,
    disableLogs: Boolean,

    showClone: Boolean,
    disableClone: Boolean,

    showViews: Boolean,
    disableViews: Boolean,

    showDownloadViews: Boolean,
    disableDownloadViews: Boolean,

    showExportUsage: Boolean,
    disableExportUsage: Boolean,

    showMarkAll: Boolean,
    disableMarkAll: Boolean,

    showSnapshot: Boolean,
    showCancelSnapshot: Boolean,

    disableCancelSnapshot: Boolean,
    disableSnapshot: Boolean,

    showCopyId: Boolean,
    disableCopyId: Boolean,

    showAddOutputTableSinkTopics: Boolean,
    disableAddOutputTableSinkTopics: Boolean,

    showAddInputTableSourceTopics: Boolean,
    disableAddInputTableSourceTopics: Boolean,

    // Table-specific actions
    showRead: Boolean,
    showUnread: Boolean,

    showTagsUpdate: Boolean,
    disableTagsUpdate: Boolean,

    showCopyKafkaBrokers: Boolean,
    disableCopyKafkaBrokers: Boolean,

    showApiTokens: Boolean,
    disableApiTokens: Boolean,

    buttonsSize: {
        type: String,
        default: 'sm'
    },

    // Menu trigger size (when variant="menu")
    menuTriggerSize: {
        type: String,
        default: 'sm',
        validator: (value) => ['sm', 'md', 'lg'].includes(value)
    }
});

const emit = defineEmits([
    'discard',
    'next',
    'back',
    'view',
    'edit',
    'save',
    'snapshot',
    'cancelSnapshot',
    'pause',
    'stop',
    'resume',
    'restart',
    'reset',
    'resetSource',
    'resetDestination',
    'delete',
    'clone',
    'logs',
    'stopSource',
    'pauseSource',
    'resumeSource',
    'pauseDestination',
    'stopDestination',
    'resumeDestination',
    'downloadViews',
    'exportUsage',
    'markAll',
    'copyId',
    'addInputTableSourceTopics',
    'addOutputTableSinkTopics',
    'views',
    // Table-specific events
    'read',
    'unread',
    'updateTags',
    'copyKafkaBrokers',
    'apiTokens'
]);

const onEmit = (event) => {
    if (!props.disabled) {
        emit(event);
    }
};

// Map button size prop to shadcn button size
const buttonSize = computed(() => {
    switch (props.buttonsSize) {
        case 'xs':
            return 'sm';
        case 'sm':
            return 'sm';
        case 'md':
            return 'default';
        case 'lg':
            return 'lg';
        case 'xl':
            return 'lg';
        default:
            return 'sm';
    }
});

// Determine if trigger should be styled as button
const shouldShowAsButton = computed(() => {
    // If explicitly set, use that value
    if (props.triggerAsButton !== null) {
        return props.triggerAsButton;
    }
    // Default: show as button only if there's a triggerLabel
    return Boolean(props.triggerLabel);
});

// Menu trigger size classes
const menuTriggerClasses = computed(() => {
    const sizeMap = {
        sm: {
            button: 'h-8 w-8',
            icon: '!h-5 !w-5'
        },
        md: {
            button: 'h-9 w-9',
            icon: '!h-6 !w-6'
        },
        lg: {
            button: 'h-10 w-10',
            icon: '!h-7 !w-7'
        }
    };
    return sizeMap[props.menuTriggerSize] || sizeMap.sm;
});
</script>
